import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatDate, parseDecimal } from "@/lib/format";
import { defaultPeriod } from "@/lib/periods";
import { PeriodFilter } from "@/components/PeriodFilter";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import ImportDialog from "@/components/ImportDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Sparkles } from "lucide-react";

const typeLabel = { FIXED: "Fixa", VARIABLE: "Variável", OTHER: "Outra" };
const typeBadge = (t) => <span className={`text-xs px-2 py-0.5 rounded font-medium ${t === "FIXED" ? "bg-sky-100 text-sky-700" : t === "VARIABLE" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-700"}`}>{typeLabel[t]}</span>;

function ExpenseDialog({ categories, onDone }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ expense_category_id: "", competence_date: today, amount: "", description: "", status: "CONFIRMED", recurrent: false, frequency: "MENSAL", count: 12 });
  const submit = async () => {
    if (!f.expense_category_id) { toast.error("Selecione a categoria"); return; }
    const amt = parseDecimal(f.amount);
    if (isNaN(amt) || amt < 0) { toast.error("Valor inválido"); return; }
    const body = { expense_category_id: f.expense_category_id, competence_date: f.competence_date, amount: amt, description: f.description, status: f.status };
    if (f.recurrent) body.recurrence = { frequency: f.frequency, count: parseInt(f.count) || 12 };
    try {
      await api.post("/expenses", body);
      toast.success(f.recurrent ? "Lançamentos recorrentes criados" : "Despesa lançada");
      setOpen(false); setF({ ...f, amount: "", description: "", recurrent: false });
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="expense-add-button"><Plus className="h-4 w-4 mr-1" /> Lançamento</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo lançamento</DialogTitle>
          <DialogDescription>Registre uma despesa por competência. Marque como recorrente para gerar lançamentos futuros automaticamente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Categoria</Label>
            <Select value={f.expense_category_id} onValueChange={(v) => setF({ ...f, expense_category_id: v })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {typeLabel[c.type]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Competência</Label><Input type="date" value={f.competence_date} onChange={(e) => setF({ ...f, competence_date: e.target.value })} data-testid="expense-competence-input" /></div>
            <div className="space-y-2"><Label>Valor</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount-input" /></div>
          </div>
          <div className="space-y-2"><Label>Descrição</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.recurrent} onCheckedChange={(v) => setF({ ...f, recurrent: !!v })} data-testid="expense-recurrent-check" /> Despesa recorrente</label>
          {f.recurrent && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Frequência</Label>
                <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
                  <SelectTrigger data-testid="expense-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MENSAL">Mensal</SelectItem><SelectItem value="TRIMESTRAL">Trimestral</SelectItem><SelectItem value="ANUAL">Anual</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Ocorrências</Label><Input type="number" min="1" value={f.count} onChange={(e) => setF({ ...f, count: e.target.value })} /></div>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={submit} data-testid="expense-save-button">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", type: "FIXED" });
  const submit = async () => {
    if (!f.name.trim()) return;
    try { await api.post("/expense-categories", f); toast.success("Categoria criada"); setOpen(false); setF({ name: "", type: "FIXED" }); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="category-add-button"><Plus className="h-4 w-4 mr-1" /> Categoria</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova categoria</DialogTitle>
          <DialogDescription>Categorias organizam as despesas e definem se entram como fixas, variáveis ou outras no DRE.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="category-name-input" /></div>
          <div className="space-y-2"><Label>Tipo</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="category-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="FIXED">Fixa</SelectItem><SelectItem value="VARIABLE">Variável</SelectItem><SelectItem value="OTHER">Outra</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="category-save-button">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleDialog({ categories, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", base_type: "NET_REVENUE", percentage: "", payment_method: "", expense_category_id: "" });
  const variableCategories = useMemo(() => categories.filter((c) => c.type === "VARIABLE"), [categories]);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Informe o nome"); return; }
    try {
      await api.post("/variable-rules", { name: f.name, base_type: f.base_type, percentage: parseDecimal(f.percentage) || 0, payment_method: f.payment_method || null, expense_category_id: f.expense_category_id || null });
      toast.success("Regra criada"); setOpen(false); setF({ name: "", base_type: "NET_REVENUE", percentage: "", payment_method: "", expense_category_id: "" }); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="rule-add-button"><Plus className="h-4 w-4 mr-1" /> Regra variável</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Regra de despesa variável automática</DialogTitle>
          <DialogDescription>Calcula uma despesa variável como percentual da receita ou de uma forma de pagamento, aplicada automaticamente ao período.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Taxa de crédito" data-testid="rule-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Base de cálculo</Label>
              <Select value={f.base_type} onValueChange={(v) => setF({ ...f, base_type: v })}>
                <SelectTrigger data-testid="rule-base-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="NET_REVENUE">Receita Líquida</SelectItem><SelectItem value="GROSS_REVENUE">Receita Bruta</SelectItem><SelectItem value="PAYMENT_METHOD">Por forma de pagamento</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Percentual %</Label><Input type="text" inputMode="decimal" placeholder="3,5" value={f.percentage} onChange={(e) => setF({ ...f, percentage: e.target.value })} data-testid="rule-pct-input" /></div>
          </div>
          {f.base_type === "PAYMENT_METHOD" && (
            <div className="space-y-2"><Label>Forma de pagamento</Label>
              <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
                <SelectTrigger data-testid="rule-payment-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent><SelectItem value="DINHEIRO">Dinheiro</SelectItem><SelectItem value="PIX">Pix</SelectItem><SelectItem value="CARTAO_DEBITO">Cartão débito</SelectItem><SelectItem value="CARTAO_CREDITO">Cartão crédito</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2"><Label>Categoria (opcional)</Label>
            <Select value={f.expense_category_id} onValueChange={(v) => setF({ ...f, expense_category_id: v })}>
              <SelectTrigger><SelectValue placeholder="Vincular a categoria" /></SelectTrigger>
              <SelectContent>{variableCategories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="rule-save-button">Salvar regra</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Despesas() {
  const { currentOrg } = useAuth();
  const [period, setPeriod] = useState(defaultPeriod());
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, c, r] = await Promise.all([
        api.get("/expenses", { params: { date_from: period.from, date_to: period.to } }),
        api.get("/expense-categories"),
        api.get("/variable-rules"),
      ]);
      setExpenses(e.data); setCategories(c.data); setRules(r.data);
    } finally { setLoading(false); }
  }, [period.from, period.to]);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const seedDefaults = async () => { try { const r = await api.post("/expense-categories/seed-defaults"); toast.success(`${r.data.created} categorias criadas`); load(); } catch (e) { toast.error(apiError(e)); } };
  const cancelExpense = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Lançamento cancelado"); load(); } catch (e) { toast.error(apiError(e)); } };
  const delRule = async (id) => { try { await api.delete(`/variable-rules/${id}`); toast.success("Regra removida"); load(); } catch (e) { toast.error(apiError(e)); } };
  const delCat = async (id) => { try { await api.delete(`/expense-categories/${id}`); toast.success("Categoria desativada"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Despesas</h1>
          <p className="text-muted-foreground mt-1">Lançamentos por competência, categorias e regras automáticas</p>
        </div>
        <ImportDialog entity="expenses" onDone={() => window.location.reload()} trigger={<Button variant="outline" data-testid="import-expenses-open"><FileSpreadsheet className="h-4 w-4 mr-2" /> Importar</Button>} />
      </div>

      <Tabs defaultValue="lancamentos">
        <TabsList>
          <TabsTrigger value="lancamentos" data-testid="tab-lancamentos">Lançamentos</TabsTrigger>
          <TabsTrigger value="categorias" data-testid="tab-categorias">Categorias</TabsTrigger>
          <TabsTrigger value="regras" data-testid="tab-regras">Regras variáveis</TabsTrigger>
        </TabsList>

        <TabsContent value="lancamentos">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <PeriodFilter value={period} onChange={setPeriod} />
            <ExpenseDialog categories={categories.filter((c) => c.status !== "INACTIVE")} onDone={load} />
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
              <Table data-testid="expenses-table">
                <TableHeader><TableRow><TableHead>Categoria</TableHead><TableHead>Tipo</TableHead><TableHead>Descrição</TableHead><TableHead>Competência</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Valor</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {expenses.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Nenhum lançamento no período</TableCell></TableRow>}
                  {expenses.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.category_name}</TableCell>
                      <TableCell>{typeBadge(e.category_type)}</TableCell>
                      <TableCell className="text-muted-foreground">{e.description || "—"}{e.recurrence_id && <span className="ml-1 text-xs text-accent">(recorrente)</span>}</TableCell>
                      <TableCell>{formatDate(e.competence_date)}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded ${e.status === "CANCELLED" ? "bg-red-100 text-red-600" : e.status === "PLANNED" ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{e.status}</span></TableCell>
                      <TableCell className="text-right font-num">{formatBRL(e.amount)}</TableCell>
                      <TableCell>{e.status !== "CANCELLED" && <Button size="icon" variant="ghost" onClick={() => cancelExpense(e.id)} data-testid={`expense-cancel-${e.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="categorias">
          <div className="flex justify-end gap-2 mb-3">
            <Button variant="outline" onClick={seedDefaults} data-testid="seed-categories-button"><Sparkles className="h-4 w-4 mr-1" /> Criar categorias padrão</Button>
            <CategoryDialog onDone={load} />
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table data-testid="categories-table">
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Tipo</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {categories.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">Nenhuma categoria. Use "Criar categorias padrão".</TableCell></TableRow>}
                {categories.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{typeBadge(c.type)}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded ${c.status === "INACTIVE" ? "bg-slate-200 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>{c.status === "INACTIVE" ? "Inativa" : "Ativa"}</span></TableCell>
                    <TableCell>{c.status !== "INACTIVE" && <Button size="icon" variant="ghost" onClick={() => delCat(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="regras">
          <div className="flex justify-end mb-3"><RuleDialog categories={categories} onDone={load} /></div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table data-testid="rules-table">
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Base</TableHead><TableHead>%</TableHead><TableHead>Pagamento</TableHead><TableHead>Ativa</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {rules.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nenhuma regra. Ex.: Taxa de crédito 3,5% das vendas em crédito.</TableCell></TableRow>}
                {rules.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.base_type === "NET_REVENUE" ? "Receita Líquida" : r.base_type === "GROSS_REVENUE" ? "Receita Bruta" : "Forma pagamento"}</TableCell>
                    <TableCell className="font-num">{r.percentage}%</TableCell>
                    <TableCell>{r.payment_method || "—"}</TableCell>
                    <TableCell>{r.active ? "Sim" : "Não"}</TableCell>
                    <TableCell><Button size="icon" variant="ghost" onClick={() => delRule(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
